import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export type FormIntakeFieldMap = {
  phone?: string;
  email?: string;
  amount?: string;
  paymentArrangement?: string;
  title?: string;
  notes?: string;
  externalSubmissionId?: string;
};

export type FormIntakeDto = {
  id: string;
  tenantId: string;
  name: string;
  externalFormId: string;
  categoryId: string;
  fieldMap: FormIntakeFieldMap;
  amountUnit: "ils" | "agorot";
  samplePayload: Record<string, unknown>;
  isActive: boolean;
  webhookUrl: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type FormIntakeDeliveryDto = {
  id: string;
  status: string;
  externalSubmissionId: string | null;
  customerId: string | null;
  subscriptionId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
};

export type FormIntakeCreatePayload = {
  name: string;
  externalFormId: string;
  categoryId: string;
  fieldMap: FormIntakeFieldMap;
  amountUnit?: "ils" | "agorot";
  samplePayload?: Record<string, unknown>;
  isActive?: boolean;
};

export type FormIntakeSuggestMappingResult = {
  fieldMap: FormIntakeFieldMap;
  aiEnhanced: boolean;
  sourceKeys: string[];
  autoDetectedTargets: Array<keyof FormIntakeFieldMap>;
  reasons: Partial<Record<keyof FormIntakeFieldMap, string>>;
};

export const formIntakeApi = {
  list(): Promise<FormIntakeDto[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FormIntakeDto[] }>("/form-intakes");
      return res.data.items;
    });
  },

  suggestMapping(
    samplePayload: Record<string, unknown>,
  ): Promise<FormIntakeSuggestMappingResult> {
    return wrap(async () => {
      const res = await httpClient.post<FormIntakeSuggestMappingResult>(
        "/form-intakes/suggest-mapping",
        { samplePayload },
      );
      return res.data;
    });
  },

  get(id: string): Promise<FormIntakeDto> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: FormIntakeDto }>(`/form-intakes/${id}`);
      return res.data.item;
    });
  },

  create(payload: FormIntakeCreatePayload): Promise<FormIntakeDto> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FormIntakeDto }>("/form-intakes", payload);
      return res.data.item;
    });
  },

  update(
    id: string,
    payload: Partial<FormIntakeCreatePayload>,
  ): Promise<FormIntakeDto> {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: FormIntakeDto }>(
        `/form-intakes/${id}`,
        payload,
      );
      return res.data.item;
    });
  },

  remove(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`/form-intakes/${id}`);
    });
  },

  rotateToken(id: string): Promise<FormIntakeDto> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FormIntakeDto }>(
        `/form-intakes/${id}/rotate-token`,
      );
      return res.data.item;
    });
  },

  listDeliveries(id: string): Promise<FormIntakeDeliveryDto[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FormIntakeDeliveryDto[] }>(
        `/form-intakes/${id}/deliveries`,
      );
      return res.data.items;
    });
  },
};
